import axios from "axios";
import {
	Activity,
	ArrowDownRight,
	ArrowUpRight,
	Award,
	Briefcase,
	Calendar,
	CalendarDays,
	CheckCircle,
	CheckSquare,
	ClipboardList,
	Clock,
	Download,
	FileCheck,
	FileText,
	Filter,
	Globe,
	GraduationCap,
	LayoutDashboard,
	MoreVertical,
	Plus,
	ShieldAlert,
	User,
	Users,
} from "lucide-react";
import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { useNavigate } from "react-router-dom";
import { PageTitle } from "../../../_metronic/layout/core";
import { DataTable } from "../../../_metronic/partials/common/DataTable";

const API_URL = import.meta.env.VITE_APP_API_URL || "";

const MiniStatCard: FC<{
	title: string;
	value: string;
	trend: string;
	icon: any;
	bg: string;
	iconColor: string;
	onClick?: () => void;
}> = ({ title, value, trend, icon: Icon, bg, iconColor, onClick }) => (
	<div
		className="card border-0 h-100 shadow-xs cursor-pointer position-relative overflow-hidden"
		onClick={onClick}
		style={{
			borderRadius: "16px",
			background: bg,
			minHeight: "120px",
			transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
		}}
		onMouseEnter={(e) => {
			e.currentTarget.style.transform = "translateY(-4px)";
			e.currentTarget.style.boxShadow = "0 12px 24px -6px rgba(0, 0, 0, 0.12)";
		}}
		onMouseLeave={(e) => {
			e.currentTarget.style.transform = "translateY(0)";
			e.currentTarget.style.boxShadow = "none";
		}}
	>
		<div className="card-body p-5 d-flex flex-column justify-content-between">
			<div className="d-flex align-items-center justify-content-between">
				<div
					className="d-flex align-items-center justify-content-center rounded-3 bg-white bg-opacity-50"
					style={{ width: "32px", height: "32px" }}
				>
					<Icon size={16} style={{ color: iconColor }} strokeWidth={2.5} />
				</div>
				<div
					className="d-flex align-items-center justify-content-center rounded-circle bg-white bg-opacity-60 p-1 me-n1 mt-n1"
					title="Click to view details"
				>
					<ArrowUpRight size={13} style={{ color: iconColor }} strokeWidth={2.5} />
				</div>
			</div>
			<div>
				<div className="text-gray-600 fw-boldest fs-7 text-uppercase mb-1">
					{title}
				</div>
				<div className="d-flex align-items-center gap-2">
					<span className="text-gray-900 fw-boldest fs-2tx tracking-tighter">
						{value}
					</span>
					<div className="bg-white bg-opacity-60 px-2 py-0.5 rounded-pill d-flex align-items-center border border-white border-opacity-50">
						<ArrowUpRight size={10} className="text-success me-1" />
						<span className="text-success fw-boldest fs-10">{trend}</span>
					</div>
				</div>
			</div>
		</div>
	</div>
);

const DetailCard: FC<{
	title: string;
	count: string;
	icon: any;
	bgGradient: string;
	onClick?: () => void;
}> = ({ title, count, icon: Icon, bgGradient, onClick }) => (
	<div
		className="card border-0 h-100 shadow-xs overflow-hidden cursor-pointer position-relative"
		onClick={onClick}
		style={{
			borderRadius: "16px",
			background: "#fff",
			transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
		}}
		onMouseEnter={(e) => {
			e.currentTarget.style.transform = "translateY(-4px)";
			e.currentTarget.style.boxShadow = "0 12px 28px -6px rgba(0, 0, 0, 0.12)";
		}}
		onMouseLeave={(e) => {
			e.currentTarget.style.transform = "translateY(0)";
			e.currentTarget.style.boxShadow = "none";
		}}
	>
		<div
			style={{ height: "80px", background: bgGradient, position: "relative" }}
		>
			<div className="position-absolute bottom-0 start-0 p-4">
				<div
					className="bg-white rounded-3 shadow-sm d-flex align-items-center justify-content-center"
					style={{ width: "36px", height: "36px" }}
				>
					<Icon size={20} className="text-gray-800" />
				</div>
			</div>
			<div className="position-absolute top-0 end-0 p-3 text-gray-700">
				<ArrowUpRight size={16} strokeWidth={2.5} />
			</div>
		</div>
		<div className="card-body p-6">
			<h4 className="fw-boldest text-gray-700 fs-6 text-uppercase mb-2 tracking-widest">
				{title}
			</h4>
			<div className="text-gray-900 fw-boldest fs-2hx tracking-tighter">
				{count}
			</div>
		</div>
	</div>
);

const DashboardWrapper: FC = () => {
	const navigate = useNavigate();
	const [metrics, setMetrics] = useState({
		total_employees: { count: 0, percentage_change: 0 },
		drp: { count: 0, percentage_change: 0 },
		vrp_staff: { count: 0, percentage_change: 0 },
		brp: { count: 0, percentage_change: 0 },
	});

	const [statusData, setStatusData] = useState({
		total: 0,
		completed: 0,
		inProgress: 0,
		pending: 0,
		others: 0,
	});

	const [trainingEventMetrics, setTrainingEventMetrics] = useState({
		training: { batches: 0, participants: 0, percentage_change: 8.2 },
		events: { total: 0, atrs: 0, percentage_change: 15.1 },
	});
	const [userContext, setUserContext] = useState<any>({ role_id: 1, district_id: null, block_id: null });

	const [progressData, setProgressData] = useState([]);
	const [districtData, setDistrictData] = useState<any[]>([]);
	const [districtSearch, setDistrictSearch] = useState("");
	const [blockData, setBlockData] = useState([]);
	const [schemes, setSchemes] = useState<any[]>([]);

	const filteredDistrictData = useMemo(() => {
		if (!districtSearch) return districtData;
		return districtData.filter((item: any) =>
			item.district?.toLowerCase().includes(districtSearch.toLowerCase()),
		);
	}, [districtData, districtSearch]);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const [
					metricsRes,
					trainingEventRes,
					statusRes,
					progressRes,
					districtRes,
					blockRes,
					schemesRes,
				] = await Promise.all([
					axios.get(`${API_URL}/admin/dashboard/metrics`),
					axios.get(`${API_URL}/admin/dashboard/training-event-metrics`),
					axios.get(`${API_URL}/admin/dashboard/scheme-status`),
					axios.get(`${API_URL}/admin/dashboard/scheme-progress`),
					axios.get(`${API_URL}/admin/dashboard/regional-summary/district?limit=100`),
					axios.get(`${API_URL}/admin/dashboard/regional-summary/block`),
					axios.get(`${API_URL}/admin/dashboard/schemes`),
				]);

				if (metricsRes.data.status) {
					setMetrics(metricsRes.data.data);
					if (metricsRes.data.data.user_context) {
						setUserContext(metricsRes.data.data.user_context);
					}
				}
				if (trainingEventRes.data.status)
					setTrainingEventMetrics(trainingEventRes.data.data);
				if (statusRes.data.status) setStatusData(statusRes.data.data);
				if (progressRes.data.status) setProgressData(progressRes.data.data);
				if (districtRes.data.status)
					setDistrictData(districtRes.data.data.items || districtRes.data.data);
				if (blockRes.data.status)
					setBlockData(blockRes.data.data.items || blockRes.data.data);
				if (schemesRes.data.status) setSchemes(schemesRes.data.data);
			} catch (error) {
				console.error("Error fetching dashboard data", error);
			}
		};
		fetchData();
	}, []);

	// Monthly Audit Trend (Main Chart)
	const auditTrendOptions: any = {
		chart: {
			fontFamily: "Inter, sans-serif",
			type: "bar",
			height: 300,
			toolbar: { show: false },
		},
		plotOptions: {
			bar: {
				borderRadius: 6,
				columnWidth: "35%",
			},
		},
		colors: ["#8b5cf6"],
		dataLabels: { enabled: false },
		xaxis: {
			categories: [
				"Jan",
				"Feb",
				"Mar",
				"Apr",
				"May",
				"Jun",
				"Jul",
				"Aug",
				"Sep",
				"Oct",
				"Nov",
				"Dec",
			],
			axisBorder: { show: false },
			axisTicks: { show: false },
			labels: { style: { colors: "#a1a5b7", fontSize: "11px" } },
		},
		yaxis: {
			labels: { style: { colors: "#a1a5b7", fontSize: "11px" } },
		},
		grid: {
			borderColor: "#eff2f5",
			strokeDashArray: 4,
			yaxis: { lines: { show: true } },
		},
		stroke: {
			show: true,
			width: 2,
			colors: ["transparent", "#f59e0b"],
			curve: "smooth",
		},
	};

	const auditTrendSeries = [
		{
			name: "Audits Completed",
			type: "column",
			data: [350, 420, 210, 310, 240, 280, 260, 450, 380, 180, 240, 210],
		},
		{
			name: "Overall Trend",
			type: "line",
			data: [280, 210, 180, 130, 210, 150, 240, 310, 180, 140, 190, 220],
		},
	];

	// Agents Status (Radial/Donut)
	const statusChartOptions: any = {
		chart: {
			fontFamily: "Inter, sans-serif",
			type: "donut",
			height: 240,
		},
		colors: ["#8b5cf6", "#f59e0b", "#10b981"],
		labels: ["Pending", "In-progress", "Completed"],
		stroke: { width: 8, colors: ["#fff"] },
		plotOptions: {
			pie: {
				donut: {
					size: "75%",
					labels: {
						show: true,
						name: {
							show: true,
							fontSize: "13px",
							fontWeight: "600",
							color: "#a1a5b7",
						},
						value: {
							show: true,
							fontSize: "24px",
							fontWeight: "900",
							color: "#181c32",
							formatter: (val: string) => val,
						},
						total: {
							show: true,
							label: "Schemes",
							fontSize: "12px",
							color: "#a1a5b7",
							formatter: function (w: any) {
								return w.globals.seriesTotals
									.reduce((a: any, b: any) => a + b, 0)
									.toString();
							},
						},
					},
				},
			},
		},
		dataLabels: { enabled: false },
		legend: { show: false },
	};

	const statusChartSeries = [
		statusData.pending,
		statusData.inProgress,
		statusData.completed,
	];

	return (
		<div
			className="animate__animated animate__fadeIn p-2"
			style={{ backgroundColor: "#f9f9fb" }}
		>
			{/* Role Context Scope Banner */}
			<div className="card border-0 shadow-xs mb-6 bg-light-primary border-primary border-opacity-25" style={{ borderRadius: "16px" }}>
				<div className="card-body p-4 d-flex align-items-center justify-content-between flex-wrap gap-3">
					<div className="d-flex align-items-center gap-3">
						<div className="p-2 bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: "36px", height: "36px" }}>
							<Globe size={18} />
						</div>
						<div>
							<h4 className="fw-boldest text-gray-900 fs-6 mb-0">
								{userContext.role_id === 3
									? `District Scope (${userContext.district_name || `District ID: ${userContext.district_id}`})`
									: userContext.role_id === 4
									? `Block Scope (${userContext.block_name || `Block ID: ${userContext.block_id}`} - ${userContext.district_name || `District ID: ${userContext.district_id}`})`
									: "State Level Social Audit Dashboard (All 39 Districts)"}
							</h4>
							<span className="text-gray-600 fs-8 fw-bold">
								{userContext.role_id === 3
									? `Displaying data strictly for ${userContext.district_name || `District ID: ${userContext.district_id}`}`
									: userContext.role_id === 4
									? `Displaying data strictly for ${userContext.block_name || `Block ID: ${userContext.block_id}`} Block`
									: "Displaying real-time aggregated metrics across Tamil Nadu"}
							</span>
						</div>
					</div>
					<span className="badge badge-primary fw-boldest fs-8 px-3 py-2 rounded-pill">
						{userContext.role_id === 3 ? "District Admin" : userContext.role_id === 4 ? "Block Admin" : "State Admin"}
					</span>
				</div>
			</div>

			<div className="row g-6 mb-8">
				{/* Top Left: 6 Small Cards */}
				<div className="col-xl-4">
					<div className="row g-4 mb-4">
						<div className="col-6">
							<MiniStatCard
								title="Total Employees"
								value={metrics.total_employees.count.toLocaleString()}
								trend={`${metrics.total_employees.percentage_change}%`}
								icon={Users}
								bg="linear-gradient(135deg, #f0f1ff 0%, #e0e2ff 100%)"
								iconColor="#6366f1"
								onClick={() => navigate("/registration")}
							/>
						</div>
						<div className="col-6">
							<MiniStatCard
								title="DRP"
								value={metrics.drp.count.toLocaleString()}
								trend={`${metrics.drp.percentage_change}%`}
								icon={Globe}
								bg="linear-gradient(135deg, #e8fff3 0%, #d1ffd6 100%)"
								iconColor="#10b981"
								onClick={() => navigate("/registration?role=3")}
							/>
						</div>
						<div className="col-6">
							<MiniStatCard
								title="VRP Staff"
								value={metrics.vrp_staff.count.toLocaleString()}
								trend={`${metrics.vrp_staff.percentage_change}%`}
								icon={User}
								bg="linear-gradient(135deg, #fff9f0 0%, #ffefd1 100%)"
								iconColor="#f59e0b"
								onClick={() => navigate("/report/vrp-registration")}
							/>
						</div>
						<div className="col-6">
							<MiniStatCard
								title="BRP"
								value={metrics.brp.count.toLocaleString()}
								trend={`${metrics.brp.percentage_change}%`}
								icon={User}
								bg="linear-gradient(135deg, #f1faff 0%, #e0f4ff 100%)"
								iconColor="#009ef7"
								onClick={() => navigate("/registration?role=4")}
							/>
						</div>
						<div className="col-6">
							<MiniStatCard
								title="Training Batches"
								value={trainingEventMetrics.training.batches.toLocaleString()}
								trend={`${trainingEventMetrics.training.percentage_change}%`}
								icon={GraduationCap}
								bg="linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)"
								iconColor="#9333ea"
								onClick={() => navigate("/training/batches")}
							/>
						</div>
						<div className="col-6">
							<MiniStatCard
								title="Events Conducted"
								value={trainingEventMetrics.events.total.toLocaleString()}
								trend={`${trainingEventMetrics.events.percentage_change}%`}
								icon={CalendarDays}
								bg="linear-gradient(135deg, #ffe4e6 0%, #fecdd3 100%)"
								iconColor="#e11d48"
								onClick={() => navigate("/event")}
							/>
						</div>
					</div>

					{/* Overall Status Bar Card */}
					<div
						className="card border-0 shadow-xs h-100 flex-fill"
						style={{ borderRadius: "16px" }}
					>
						<div className="card-body p-6">
							<div className="d-flex align-items-center justify-content-between mb-4">
								<span className="text-gray-500 fw-boldest fs-8">
									AUDIT PROGRESS STATUS
								</span>
								<div className="p-2 bg-light-purple rounded-3">
									<LayoutDashboard size={14} className="text-primary" />
								</div>
							</div>
							<div className="d-flex align-items-end gap-2 mb-6">
								<span className="text-gray-900 fw-boldest fs-2">45</span>
								<span className="text-gray-400 fs-6 fw-bold mb-1">
									/ 85 Completed
								</span>
							</div>
							<div className="progress h-8px bg-light-purple rounded-pill overflow-hidden mb-2">
								<div
									className="progress-bar bg-primary"
									style={{ width: "53%" }}
								></div>
							</div>
						</div>
					</div>
				</div>

				{/* Top Right: Large Performance Chart */}
				<div className="col-xl-8">
					<div
						className="card border-0 shadow-xs"
						style={{ borderRadius: "16px" }}
					>
						<div className="card-header border-0 pt-7 px-8">
							<div className="d-flex align-items-center justify-content-between w-100">
								<h3 className="card-title d-flex align-items-center mb-0">
									<Activity size={18} className="text-gray-800 me-2" />
									<span className="card-label fw-boldest fs-5 text-gray-800">
										Audit Performance Insights
									</span>
								</h3>
								<div className="d-flex align-items-center gap-2">
									<button className="btn btn-sm btn-light border px-4 py-2 d-flex align-items-center gap-2">
										<Calendar size={14} className="text-gray-500" />
										<span className="fs-8 fw-bold">Monthly</span>
									</button>
								</div>
							</div>
							<div className="mt-4 d-flex align-items-center gap-4">
								<div className="d-flex align-items-center gap-2">
									<span className="text-gray-400 fs-8 fw-bold">
										Audit Completed:
									</span>
									<span className="text-gray-900 fs-5 fw-boldest">
										$85,245.00
									</span>
									<span className="badge badge-light-success fs-9">+21%</span>
								</div>
							</div>
						</div>
						<div className="card-body">
							<Chart
								options={auditTrendOptions}
								series={auditTrendSeries}
								type="bar"
								height={300}
							/>

							<div className="row g-8 mt-4">
								<div className="col-4">
									<div className="d-flex align-items-center justify-content-between mb-2">
										<span className="text-gray-500 fs-8 fw-bold">DRP</span>
										<span className="text-gray-900 fs-8 fw-boldest">
											{metrics.drp.count.toLocaleString()}
										</span>
									</div>
									<div className="progress h-6px rounded-pill bg-light-primary">
										<div
											className="progress-bar bg-primary"
											style={{
												width: `${metrics.total_employees.count > 0 ? Math.round((metrics.drp.count / metrics.total_employees.count) * 100) : 0}%`,
											}}
										></div>
									</div>
								</div>
								<div className="col-4">
									<div className="d-flex align-items-center justify-content-between mb-2">
										<span className="text-gray-500 fs-8 fw-bold">BRP </span>
										<span className="text-gray-900 fs-8 fw-boldest">
											{metrics.brp.count.toLocaleString()}
										</span>
									</div>
									<div className="progress h-6px rounded-pill bg-light-warning">
										<div
											className="progress-bar bg-warning"
											style={{
												width: `${metrics.total_employees.count > 0 ? Math.round((metrics.brp.count / metrics.total_employees.count) * 100) : 0}%`,
											}}
										></div>
									</div>
								</div>
								<div className="col-4">
									<div className="d-flex align-items-center justify-content-between mb-2">
										<span className="text-gray-500 fs-8 fw-bold">VRP</span>
										<span className="text-gray-900 fs-8 fw-boldest">
											{metrics.vrp_staff.count.toLocaleString()}
										</span>
									</div>
									<div className="progress h-6px rounded-pill bg-light-success">
										<div
											className="progress-bar bg-success"
											style={{
												width: `${metrics.total_employees.count > 0 ? Math.round((metrics.vrp_staff.count / metrics.total_employees.count) * 100) : 0}%`,
											}}
										></div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Middle Row: Content & Status */}
			<div className="row g-8 mb-8">
				<div className="col-xl-8">
					<div
						className="card border-0 shadow-xs h-100"
						style={{ borderRadius: "16px" }}
					>
						<div className="card-header border-0 pt-7 px-8">
							<h3 className="card-title d-flex align-items-center">
								<FileText size={18} className="text-gray-800 me-2" />
								<span className="card-label fw-boldests fs-5 text-gray-800">
									Scheme Progress Detail
								</span>
							</h3>
						</div>
						<div className="card-body">
							<Chart
								options={{
									chart: { stacked: true, toolbar: { show: false } },
									plotOptions: { bar: { columnWidth: "30%", borderRadius: 8 } },
									colors: [
										"#8b5cf6",
										"#c4b5fd",
										"#ddd6fe",
										"#ede9fe",
										"#f5f3ff",
									],
									grid: { show: false },
									xaxis: {
										labels: { show: false },
										axisBorder: { show: false },
										axisTicks: { show: false },
									},
									yaxis: { labels: { show: false } },
									legend: { position: "bottom", horizontalAlign: "center" },
								}}
								series={
									schemes.length > 0
										? schemes.map((scheme: any) => ({
												name: scheme.name,
												data: progressData.map((d: any) => d[scheme.name] || 0),
											}))
										: []
								}
								type="bar"
								height={300}
							/>
						</div>
					</div>
				</div>
				<div className="col-xl-4">
					<div
						className="card border-0 shadow-xs h-100"
						style={{ borderRadius: "16px" }}
					>
						<div className="card-header border-0 pt-7 px-8">
							<h3 className="card-title d-flex align-items-center">
								<ShieldAlert size={18} className="text-gray-800 me-2" />
								<span className="card-label fw-boldest fs-5 text-gray-800">
									Scheme Status
								</span>
							</h3>
						</div>
						<div className="card-body d-flex flex-column align-items-center justify-content-center">
							<Chart
								options={statusChartOptions}
								series={statusChartSeries}
								type="donut"
								height={260}
							/>
							<div className="row w-100 mt-8 g-4">
								<div className="col-6 text-center">
									<div className="d-flex align-items-center justify-content-center gap-2 mb-1">
										<div
											className="rounded-circle bg-primary"
											style={{ width: "8px", height: "8px" }}
										></div>
										<span className="text-gray-900 fw-boldest fs-6">
											{statusData.completed}
										</span>
									</div>
									<span className="text-gray-400 fs-9 fw-bold">Completed</span>
								</div>
								<div className="col-6 text-center">
									<div className="d-flex align-items-center justify-content-center gap-2 mb-1">
										<div
											className="rounded-circle bg-warning"
											style={{ width: "8px", height: "8px" }}
										></div>
										<span className="text-gray-900 fw-boldest fs-6">
											{statusData.inProgress}
										</span>
									</div>
									<span className="text-gray-400 fs-9 fw-bold">
										In-progress
									</span>
								</div>
								<div className="col-6 text-center">
									<div className="d-flex align-items-center justify-content-center gap-2 mb-1">
										<div
											className="rounded-circle bg-danger"
											style={{ width: "8px", height: "8px" }}
										></div>
										<span className="text-gray-900 fw-boldest fs-6">
											{statusData.pending}
										</span>
									</div>
									<span className="text-gray-400 fs-9 fw-bold">Pending</span>
								</div>
								<div className="col-6 text-center">
									<div className="d-flex align-items-center justify-content-center gap-2 mb-1">
										<div
											className="rounded-circle bg-gray-300"
											style={{ width: "8px", height: "8px" }}
										></div>
										<span className="text-gray-900 fw-boldest fs-6">
											{statusData.others}
										</span>
									</div>
									<span className="text-gray-400 fs-9 fw-bold">Others</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Detail Row: Counts Instead of Quick Actions */}
			<div className="row g-6 mb-8">
				<div className="col-xl-3">
					<DetailCard
						title="Total scheme"
						count={statusData.total.toString()}
						icon={ClipboardList}
						bgGradient="linear-gradient(135deg, #ddd6fe 0%, #c4b5fd 100%)"
						onClick={() => navigate("/scheme-data-entry")}
					/>
				</div>
				<div className="col-xl-3">
					<DetailCard
						title="Pending"
						count={statusData.pending.toString()}
						icon={Clock}
						bgGradient="linear-gradient(135deg, #fff5f8 0%, #ffcbd1 100%)"
						onClick={() => navigate("/scheme-data-entry?status=pending")}
					/>
				</div>
				<div className="col-xl-3">
					<DetailCard
						title="Inprogress"
						count={statusData.inProgress.toString()}
						icon={Activity}
						bgGradient="linear-gradient(135deg, #fffbeb 0%, #ffefd1 100%)"
						onClick={() => navigate("/scheme-data-entry?status=inprogress")}
					/>
				</div>
				<div className="col-xl-3">
					<DetailCard
						title="Completed"
						count={statusData.completed.toString()}
						icon={CheckSquare}
						bgGradient="linear-gradient(135deg, #f0fff4 0%, #d1ffd6 100%)"
						onClick={() => navigate("/scheme-data-entry?status=completed")}
					/>
				</div>
			</div>

			{/* Bottom Row: Tables */}
			<div className="row g-8">
				<div className="col-12">
					<div
						className="card border-0 shadow-xs h-100"
						style={{ borderRadius: "16px" }}
					>
						<div className="card-header border-0 pt-7 px-8 pb-4">
							<div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between w-100 gap-3">
								<div className="d-flex align-items-center gap-3">
									<h3 className="card-title text-gray-800 fw-boldest fs-4 mb-0">
										{userContext.role_id === 3
											? `Block Wise Overview — ${userContext.district_name || 'District'}`
											: userContext.role_id === 4
											? `Block Overview — ${userContext.block_name || 'Block'}`
											: "District Wise Overview"}
									</h3>
									<span className="badge badge-light-primary fw-boldest fs-8 px-3 py-2 rounded-pill">
										{userContext.role_id === 3 || userContext.role_id === 4
											? `${blockData.length} ${blockData.length === 1 ? 'Block' : 'Blocks'}`
											: `${filteredDistrictData.length} Districts`}
									</span>
								</div>
								<div className="d-flex align-items-center gap-3">
									<div className="position-relative">
										<input
											type="text"
											className="form-control form-control-solid ps-9 py-2"
											placeholder={userContext.role_id === 3 || userContext.role_id === 4 ? "Search block..." : "Search district..."}
											value={districtSearch}
											onChange={(e) => setDistrictSearch(e.target.value)}
											style={{ width: "200px", borderRadius: "10px" }}
										/>
										<Filter
											size={14}
											className="position-absolute top-50 start-0 translate-middle-y ms-3 text-gray-500"
										/>
									</div>
									<button
										className="btn btn-sm btn-light-primary border px-4 py-2 fw-boldest d-flex align-items-center gap-1"
										onClick={() => navigate("/report/staff-registration")}
									>
										View Full Report
										<ArrowUpRight size={14} />
									</button>
								</div>
							</div>
						</div>
						<div className="card-body px-4 py-2">
							<DataTable
								data={
									userContext.role_id === 3 || userContext.role_id === 4
										? districtSearch
											? blockData.filter((b: any) => b.block?.toLowerCase().includes(districtSearch.toLowerCase()))
											: blockData
										: filteredDistrictData
								}
								columns={[
									{
										header: userContext.role_id === 3 || userContext.role_id === 4 ? "Block" : "District",
										accessorKey: userContext.role_id === 3 || userContext.role_id === 4 ? "block" : "district",
										cell: (info: any) => (
											<div
												className="d-flex align-items-center gap-2 cursor-pointer text-primary fw-boldest hover-opacity-75"
												onClick={() =>
													navigate(
														userContext.role_id === 3 || userContext.role_id === 4
															? `/registration?block=${info.row.original.id}&blockName=${encodeURIComponent(info.row.original.block)}`
															: `/registration?district=${info.row.original.id}&districtName=${encodeURIComponent(info.row.original.district)}`,
													)
												}
												title={userContext.role_id === 3 || userContext.role_id === 4 ? "Click to view block staff" : "Click to view district staff"}
											>
												<Globe size={14} className="text-primary opacity-75" />
												<span>{info.getValue()}</span>
											</div>
										),
									},
									{
										header: "Total",
										accessorKey: "total",
										cell: (info: any) => {
											const count = Number(info.getValue() || 0);
											return (
												<span
													className="badge badge-light-primary fw-boldest fs-7 cursor-pointer"
													style={count > 0 ? { textDecoration: "underline", textUnderlineOffset: "3px" } : {}}
													onClick={() =>
														navigate(
															userContext.role_id === 3 || userContext.role_id === 4
																? `/registration?block=${info.row.original.id}&blockName=${encodeURIComponent(info.row.original.block)}`
																: `/registration?district=${info.row.original.id}&districtName=${encodeURIComponent(info.row.original.district)}`,
														)
													}
													title="View all staff"
												>
													{count}
												</span>
											);
										},
									},
									{
										header: "Status Breakdown",
										columns: [
											{
												header: "Filled",
												accessorKey: "filled",
												cell: (info: any) => {
													const count = Number(info.getValue() || 0);
													return (
														<span
															className="badge badge-light-success fw-boldest fs-7 cursor-pointer"
															style={count > 0 ? { textDecoration: "underline", textUnderlineOffset: "3px" } : {}}
															onClick={() =>
																navigate(
																	userContext.role_id === 3 || userContext.role_id === 4
																		? `/registration?block=${info.row.original.id}&blockName=${encodeURIComponent(info.row.original.block)}&status=Completed`
																		: `/registration?district=${info.row.original.id}&districtName=${encodeURIComponent(info.row.original.district)}&status=Completed`,
																)
															}
															title="View completed staff"
														>
															{count}
														</span>
													);
												},
											},
											{
												header: "In-progress",
												accessorKey: "inProgress",
												cell: (info: any) => {
													const count = Number(info.getValue() || 0);
													return (
														<span
															className="badge badge-light-warning fw-boldest fs-7 cursor-pointer"
															style={count > 0 ? { textDecoration: "underline", textUnderlineOffset: "3px" } : {}}
															onClick={() =>
																navigate(
																	userContext.role_id === 3 || userContext.role_id === 4
																		? `/registration?block=${info.row.original.id}&blockName=${encodeURIComponent(info.row.original.block)}&status=In progress`
																		: `/registration?district=${info.row.original.id}&districtName=${encodeURIComponent(info.row.original.district)}&status=In progress`,
																)
															}
															title="View in-progress staff"
														>
															{count}
														</span>
													);
												},
											},
										],
									},
								]}
								hideSearchInput
								hideExport
								className="border-0 shadow-none"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default DashboardWrapper;
